import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Group, NumberInput, Text } from '@mantine/core';
import { formatMoney } from '@/lib/format';
import { PRICE_MIN, isShiftValid, maxShift, priceListFieldId, shiftPreview, toCell, type PriceCell, type PriceDraft } from './priceListForm';

interface ShiftLineProps {
  readonly draft: PriceDraft;
  readonly onApply: (step: number) => void;
}

// Заполнение цен со 100 % предоплатой: «без предоплаты − шаг» по всем строкам с ценой без предоплаты. Строка видна
// постоянно, под ней — что именно будет заполнено и сколько уже введённых цен заменится (решение 2026-11-04).
export default function ShiftLine({ draft, onApply }: ShiftLineProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<PriceCell>('');
  const max = maxShift(draft);
  // Самая низкая цена без предоплаты уже на минимуме: уменьшать её нечем.
  const noRoom = max != null && max < 1;
  const preview = shiftPreview(draft);
  const tooLarge = step !== '' && max != null && max >= 1 && step > max;
  const inputId = priceListFieldId('shift');

  return (
    <div>
      <Group gap="xs" wrap="wrap" align="center">
        <Text component="label" htmlFor={inputId} size="sm">
          {t('app.buy.form.shiftLabel')}
        </Text>
        <NumberInput
          id={inputId}
          value={step}
          onChange={(value) => setStep(toCell(value))}
          min={1}
          allowNegative={false}
          allowDecimal={false}
          thousandSeparator=" "
          clampBehavior="none"
          hideControls
          w={120}
          rightSection={<Text size="sm" c="gray.7">{t('app.buy.form.perTon')}</Text>}
          rightSectionWidth={44}
          disabled={max == null || noRoom}
          error={tooLarge}
          aria-describedby={`${inputId}-hint`}
        />
        <Button
          variant="default"
          disabled={!isShiftValid(step, max)}
          onClick={() => {
            if (isShiftValid(step, max)) {
              onApply(step);
            }
          }}
        >
          {t('app.buy.form.shiftApply')}
        </Button>
      </Group>
      <Text id={`${inputId}-hint`} size="sm" c={tooLarge ? 'loss.8' : 'gray.7'} mt={6} aria-live="polite">
        {max == null
          ? t('app.buy.form.shiftNeedBase')
          : noRoom
            ? t('app.buy.form.shiftNoRoom', { min: formatMoney(PRICE_MIN) })
            : tooLarge
            ? t('app.buy.form.shiftTooLarge', { max: formatMoney(max), min: formatMoney(PRICE_MIN) })
            : preview.replace > 0
              ? // Параметр назван `replaced`: `replace` в i18next зарезервирован под набор подстановок и стёр бы числа.
                t('app.buy.form.shiftPreviewReplace', { count: preview.fill, replaced: preview.replace })
              : t('app.buy.form.shiftPreview', { count: preview.fill })}
      </Text>
    </div>
  );
}
